'use client';

import React, { useState, useEffect } from 'react'
import { ICONS, IMAGES } from '@/constants/constants'
import Image from 'next/image'
import Input from '@/components/atomic/Input'
import Dropdown from '@/components/atomic/Dropdown'
import { useValidation } from '@/hooks/useValidation'
import { ExclamationCircleIcon } from '@heroicons/react/24/solid'
import { submitContactForm } from '@/lib/api/contact'
import { useToastContext } from '@/components/ui/ToastProvider';
import { LoadingButton } from '@/components/atomic';
import { useAppSelector } from '@/lib/hooks/redux';

const ContactUs = () => {

    const { toast } = useToastContext();
    const { user } = useAppSelector(state => state.auth);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        subject: '',
        message: ''
    });

    // Scroll to top when component mounts
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    // Prefill name and email if user is logged in
    useEffect(() => {
        if (user) {
            setFormData(prev => ({
                ...prev,
                name: user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : prev.name,
                email: user.email || prev.email
            }));
        }
    }, [user]);

    const subjectOptions = [
        { value: 'General Inquiry', label: 'General Inquiry' },
        { value: 'Fare Question', label: 'Fare Question' },
        { value: 'Medical Trip Question', label: 'Medical Trip Question' }
    ];
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formKey, setFormKey] = useState(0);
    const { validate } = useValidation();

    const contactInfo = [
        {
            id: 'phone',
            icon: (
                <Image src={ICONS.phone} height={15} width={15} alt='phoen' />
            ),
            text: '250-625-2445'
        },
        {
            id: 'email',
            icon: (
                <Image src={ICONS.email} height={15} width={15} alt='phoen' />
            ),
            text: 'reservations_nicola@laxband.com'
        },
        {
            id: 'address',
            icon: (
                <Image src={ICONS.location} height={15} width={15} alt='phoen' />
            ),
            text: "The Spirit of Lax Kw'alaams Ferry Ramp, Aero Point, Prince Rupert, BC V8J 3P9"
        }
    ];

    const socialLinks = [

        {
            id: 'facebook',
            bgColor: 'bg-red-500',
            icon: (
                <Image src={ICONS.facebookSocial} height={16} width={16} alt='facebook' />


            ),
            link: "https://www.facebook.com/laxkwalaamsband"
        },


        {
            id: 'instagram',
            bgColor: 'bg-red-500',
            icon: (
                <Image
                    src={ICONS.instagram}
                    alt="instagram"
                    width={16}
                    height={16}
                />
            ),
            link: "https://www.instagram.com/laxkwalaamsband/"
        }
    ];

    const handleInputChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        setFormData(prev => ({
            ...prev,
            [field]: e.target.value
        }));

        // Clear error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: ''
            }));
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const validationErrors = validate(formData, {
            requireNames: ['name', 'subject', 'message']
        });

        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        setIsSubmitting(true);

        try {
            const response = await submitContactForm({
                name: formData.name,
                email: formData.email,
                subject: formData.subject,
                message: formData.message
            });

            if (response.success) {
                toast.success(response?.message);
                setFormData({ name: '', email: '', subject: '', message: '' });
                setErrors({});
                setFormKey(prev => prev + 1); // Force form re-render
            } else {
                toast.error('Failed to send message', response.message || 'Something went wrong. Please try again.');
            }
        } catch (error: any) {
            toast.error(
                'Failed to send message',
                error.response?.data?.message || 'Please check your connection and try again.'
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div>
            <div className="flex  justify-center items-center flex-col max-w-7xl mx-auto px-4 py-8 max-md:py-4 max-md:px-0">
                <Image
                    className='w-full rounded-lg mb-12 max-md:hidden'
                    src={IMAGES.contact}
                    alt="contact"
                    width={1000}
                    height={400}
                />


                <div className='flex flex-col-reverse lg:flex-row gap-12 w-full '>
                    {/* Info Section */}
                    <div className="lg:w-1/2 max-md:px-4">
                        <div className=" flex flex-col gap-10">
                            <div className='flex flex-col gap-2'>
                                <h2 className="text-3xl font-rakesly font-bold text-gray-800 max-md:text-2xl">GET IN TOUCH</h2>
                                <p className="text-gray-600 max-md:text-sm">Have questions about our ferry services? We're here to help!</p>
                            </div>

                            <div className="space-y-6 max-md:space-y-3">
                                {contactInfo.map((item) => (
                                    <div key={item.id} className="flex items-center gap-1 max-md:text-sm">
                                        <div className="w-6 h-6 ">
                                            {item.icon}
                                        </div>
                                        <div>
                                            <p className="font-medium text-gray-800 max-md:text-xs">{item.text}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className="">
                                <p className="text-gray-600 mb-4">Follow Us</p>
                                <div className="flex space-x-4">
                                    {socialLinks.map((social) => (
                                        <a target='_blank' href={social.link} key={social.id} className={`w-10 h-10 max-md:w-6 max-md:h-6 ${social.bgColor} rounded-full flex items-center justify-center cursor-pointer hover:opacity-80 transition-opacity`}>
                                            {social.icon}
                                        </a>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Contact Input Section */}
                    <div className="lg:w-1/2">
                        <div className="bg-white rounded-lg shadow-lg p-6 max-md:rounded-none">
                            <h2 className="text-2xl font-bold text-gray-800 mb-6">Send us a Message</h2>



                            <form onSubmit={handleSubmit} noValidate className="space-y-6 font-work-sans" key={formKey}>
                                <Input
                                    label="Name"
                                    type="text"
                                    labelClassName='!mb-0 !text-gray-600 font-normal'
                                    value={formData.name}
                                    onChange={handleInputChange('name')}
                                    error={errors.name}
                                // placeholder="Your full name"
                                />

                                <Input
                                    label="Email"
                                    type="text"
                                    labelClassName='!mb-0 !text-gray-600 font-normal'
                                    value={formData.email}
                                    onChange={handleInputChange('email')}
                                    error={errors.email}
                                // placeholder="your.email@example.com"
                                />

                                <Dropdown
                                    label="Subject"
                                    options={subjectOptions}
                                    value={formData.subject}
                                    onChange={handleInputChange('subject')}
                                    error={errors.subject}
                                    labelClassName='!mb-0 !text-gray-600 font-normal'
                                    placeholder="Select a subject"
                                />

                                <div className="w-full font-sans font-normal">
                                    <label className="block text-sm text-gray-600 font-normal">
                                        Message
                                    </label>
                                    <div className="relative">
                                        <textarea
                                            value={formData.message}
                                            onChange={handleInputChange('message')}
                                            rows={6}
                                            className={`
                                                w-full px-4 py-2 border border-gray-300 rounded-lg
                                                focus:ring-2 focus:ring-blue-500 focus:border-transparent
                                                transition-all duration-200 outline-none resize-vertical
                                                ${errors.message ? 'border-red-500 focus:ring-red-500 pr-10' : ''}
                                            `}
                                        // placeholder="Tell us how we can help you..."
                                        />
                                        {errors.message && (
                                            <ExclamationCircleIcon
                                                className="h-5 w-5 text-red-500 absolute right-3 top-3 pointer-events-none"
                                            />
                                        )}
                                    </div>
                                    {errors.message && (
                                        <p className="mt-1 text-sm text-red-600">{errors.message}</p>
                                    )}
                                </div>

                                <LoadingButton
                                    type="submit"
                                    loading={isSubmitting}
                                    // disabled={isSubmitting}
                                    className="w-full bg-gray-800 text-white py-3 px-6 rounded-lg font-medium hover:bg-gray-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {isSubmitting ? 'Sending...' : 'Send Message'}
                                </LoadingButton>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default ContactUs